import { useState } from "react";
import {
  useRecentIncidents,
  useTopProblemCameras,
  useTopProblemZones,
  useUptime,
} from "../../hooks/useVideoHistory";
import { formatDuration, fmtTime, lotLabel } from "../../lib/historyFormat";
import {
  DEFAULT_TIME_RANGE,
  timeRangeLabel,
  type TimeRange,
} from "../../lib/timeRange";
import {
  EmptyHint,
  FilterRow,
  LotHeader,
  Pager,
  SectionCard,
  SegToggle,
  TimeRangePicker,
  UptimeRow,
  useUptimeBarDays,
  groupBy,
} from "./HistoryPrimitives";

const PAGE_SIZE = 20;

/**
 * 영상분석 이력 — 주차장(lot) > 구역(zone) 계층.
 * 데이터는 `/api/video-history/*` (incident/daily 집계 + 카메라 조인).
 */
export default function VideoHistorySections() {
  return (
    <div className="space-y-4">
      <Uptime />
      <ProblemZones />
      <ProblemCameras />
      <Timeline />
    </div>
  );
}

function ProblemZones() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const [page, setPage] = useState(0);
  const { data, isLoading } = useTopProblemZones(range, page, PAGE_SIZE);
  const zones = data?.items ?? [];
  const byLot = groupBy(zones, (z) => `${z.lotId}|${z.lotName ?? ""}`);

  const onRange = (r: TimeRange) => {
    setRange(r);
    setPage(0); // 기간 변경 시 첫 페이지로
  };

  return (
    <SectionCard
      title="문제 많은 구역"
      hint="총 지속 많은 순"
      info={{
        summary:
          "선택 기간 동안 '문제'가 가장 잦았던 구역을 누적 지속 시간 순으로 보여줍니다.",
        basis:
          "'문제' = 영상분석 카운트와 운영 API 카운트의 불일치가 2분(120초) 이상 지속된 사건(incident). 그 구역의 incident 들을 모아 집계합니다.",
        rows: [
          { term: "문제 N회", desc: "그 구역이 문제 상태로 진입한 횟수" },
          { term: "총 X", desc: "문제 상태로 있던 누적 시간" },
          { term: "코드 · [A4]", desc: "앞 = 카메라 ID, 뒤 badge = 구역 이름" },
        ],
        legend: [
          { swatch: "bg-status-majorOutage", label: "빨강 점 — 문제 집계 대상 표식" },
        ],
        period: "상단 기간 선택 기준 (기본 최근 30일 · 1~365일/사용자 지정)",
      }}
    >
      <div className="mb-3">
        <TimeRangePicker value={range} onChange={onRange} />
      </div>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : zones.length === 0 ? (
        <EmptyHint text={`${timeRangeLabel(range)} 동안 문제가 기록되지 않았습니다.`} />
      ) : (
        <div className="space-y-4">
          {[...byLot.values()].map((rows) => (
            <div key={rows[0].lotId}>
              <LotHeader label={lotLabel(rows[0].lotName, rows[0].lotId)} sub={rows[0].lotId} />
              <ul className="space-y-2">
                {rows.map((z) => (
                  <li
                    key={z.zone}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-surface-border px-3 py-2 dark:border-surface-darkBorder"
                  >
                    {/* 카메라 = 주 라벨 (없으면 구역명으로 폴백) */}
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className="inline-block h-2 w-2 rounded-full bg-status-majorOutage dark:bg-status-majorOutage-dark"
                      />
                      {z.cameras.length > 0 ? (
                        <span className="flex flex-wrap items-center gap-x-1.5">
                          {z.cameras.map((c) => (
                            <span
                              key={c}
                              className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100"
                            >
                              {c}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {z.zone}
                        </span>
                      )}
                    </span>
                    {/* 구역 = 보조 badge (카메라가 있을 때만) */}
                    {z.cameras.length > 0 && (
                      <span className="rounded-full border border-surface-border bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-surface-darkBorder dark:bg-slate-800/50 dark:text-slate-300">
                        {z.zone}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                      문제 <span className="font-medium tabular-nums">{z.incidentCount}</span>회 ·
                      총 <span className="font-medium">{formatDuration(z.totalProblemSeconds)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {data && (
        <Pager
          page={data.page}
          size={data.size}
          total={data.total}
          hasNext={data.hasNext}
          onPage={setPage}
        />
      )}
    </SectionCard>
  );
}

/**
 * 문제 많은 카메라 — 한 incident 의 cameras 를 펼쳐 카메라 단위로 집계.
 * '문제 횟수가 많은' 카메라와 '누적 문제 시간이 긴' 카메라를 한눈에 보기 위함.
 * 백엔드가 누적 시간 기준 desc 정렬해서 반환. 한 카메라가 여러 구역에 걸치면 zones 로 모은다.
 */
function ProblemCameras() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const [page, setPage] = useState(0);
  const { data, isLoading } = useTopProblemCameras(range, page, PAGE_SIZE);
  const cameras = data?.items ?? [];

  const onRange = (r: TimeRange) => {
    setRange(r);
    setPage(0);
  };

  return (
    <SectionCard
      title="문제 많은 카메라"
      hint="총 지속 많은 순"
      info={{
        summary:
          "문제(incident)에 등장한 카메라를 카메라 단위로 펼쳐 누적 지속 시간 순으로 보여줍니다.",
        basis:
          "한 incident 의 카메라 매핑을 카메라별로 분해해 합산합니다. 한 카메라가 여러 구역에 걸칠 수 있습니다.",
        rows: [
          { term: "문제 N회", desc: "그 카메라가 incident 에 등장한 횟수" },
          { term: "총 X", desc: "그 카메라가 낀 incident 들의 누적 문제 시간" },
          { term: "[A4] badge", desc: "그 카메라가 속한 구역(복수 가능)" },
        ],
        legend: [
          { swatch: "bg-status-majorOutage", label: "빨강 점 — 문제 집계 대상 표식" },
        ],
        period: "상단 기간 선택 기준 (기본 최근 30일 · 1~365일/사용자 지정)",
      }}
    >
      <div className="mb-3">
        <TimeRangePicker value={range} onChange={onRange} />
      </div>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : cameras.length === 0 ? (
        <EmptyHint text={`${timeRangeLabel(range)} 동안 카메라 문제가 기록되지 않았습니다.`} />
      ) : (
        <ul className="space-y-2">
          {cameras.map((c) => (
            <li
              key={c.camera}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-surface-border px-3 py-2 dark:border-surface-darkBorder"
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full bg-status-majorOutage dark:bg-status-majorOutage-dark"
                />
                <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {c.camera}
                </span>
              </span>
              {c.zones.length > 0 && (
                <span className="flex flex-wrap items-center gap-1">
                  {c.zones.map((z) => (
                    <span
                      key={z}
                      className="rounded-full border border-surface-border bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-surface-darkBorder dark:bg-slate-800/50 dark:text-slate-300"
                    >
                      {z}
                    </span>
                  ))}
                </span>
              )}
              <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                문제 <span className="font-medium tabular-nums">{c.incidentCount}</span>회 ·
                총 <span className="font-medium">{formatDuration(c.totalProblemSeconds)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {data && (
        <Pager
          page={data.page}
          size={data.size}
          total={data.total}
          hasNext={data.hasNext}
          onPage={setPage}
        />
      )}
    </SectionCard>
  );
}

function Uptime() {
  const [page, setPage] = useState(0);
  // 화면 폭에 따른 표시 일수 — 헤더 문구를 막대와 일치시키기 위해 함께 쓴다.
  const barDays = useUptimeBarDays();
  const { data, isLoading } = useUptime(90);
  const zones = data ?? [];
  const byLot = groupBy(zones, (z) => `${z.lotId}|${z.lotName ?? ""}`);
  const allLots = [...byLot.values()];
  const total = allLots.length;
  const start = page * PAGE_SIZE;
  const pageItems = allLots.slice(start, start + PAGE_SIZE);
  const hasNext = start + PAGE_SIZE < total;

  return (
    <SectionCard
      title="구역별 가동률"
      hint={`최근 ${barDays}일 · 막대 1개 = 하루`}
      info={{
        summary:
          "구역이 하루 중 '문제(PROBLEM)'가 아니었던 시간 비율입니다. 막대 1개 = 하루.",
        basis:
          "매일 자정 직후 전날의 문제(불일치 2분+ 지속) 구간을 합산해 (86400초 − 문제초) / 86400 으로 계산합니다. 주차/출차 예상 등 정상 상태는 가동 시간으로 칩니다.",
        legend: [
          { swatch: "bg-status-operational", label: "초록 — 가동률 99.9% 이상" },
          { swatch: "bg-status-degraded", label: "주황 — 95% ~ 99.9%" },
          { swatch: "bg-status-majorOutage", label: "빨강 — 95% 미만" },
          { swatch: "bg-slate-400", label: "진한 회색 — 연결 끊김(GPU 무신호 12시간+)" },
          { swatch: "bg-slate-200", label: "옅은 회색 — 기록 없음(집계 전·등록 전)" },
        ],
        period: `최근 ${barDays}일 고정 · KST 기준 · 우측 '평균/기록'은 끊김 날 제외`,
      }}
    >
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : total === 0 ? (
        <EmptyHint text="아직 일별 요약이 없습니다 (매일 자정 집계)." />
      ) : (
        <div className="space-y-4">
          {pageItems.map((rows) => (
            <div key={rows[0].lotId}>
              <LotHeader label={lotLabel(rows[0].lotName, rows[0].lotId)} sub={rows[0].lotId} />
              <div className="space-y-3">
                {rows.map((z) => (
                  <UptimeRow key={z.zone} label={z.zone} days={z.days} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {total > 0 && (
        <Pager
          page={page}
          size={PAGE_SIZE}
          total={total}
          hasNext={hasNext}
          onPage={setPage}
        />
      )}
    </SectionCard>
  );
}

type VideoStatusFilter = "all" | "ongoing" | "ended";
type VideoRecentDaysFilter = 1 | 3 | 7 | 30 | 0; // 0 = 전체
type VideoRecentLimitFilter = 20 | 50;

function withinDays(iso: string, days: number, now: number): boolean {
  if (days <= 0) return true;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return true;
  return now - ms <= days * 24 * 60 * 60 * 1000;
}

function Timeline() {
  const [statusFilter, setStatusFilter] = useState<VideoStatusFilter>("all");
  const [daysFilter, setDaysFilter] = useState<VideoRecentDaysFilter>(7);
  const [limitFilter, setLimitFilter] = useState<VideoRecentLimitFilter>(20);
  const { data, isLoading } = useRecentIncidents(50);
  const raw = data ?? [];
  const now = Date.now();
  const incidents = raw
    .filter((i) => {
      if (statusFilter === "ongoing" && !i.ongoing) return false;
      if (statusFilter === "ended" && i.ongoing) return false;
      if (!withinDays(i.startedAt, daysFilter, now)) return false;
      return true;
    })
    .slice(0, limitFilter);

  return (
    <SectionCard
      title="최근 이력"
      hint="문제 발생 구간"
      info={{
        summary:
          "문제(불일치 2분+ 지속)가 발생했던 개별 구간을 최신순으로 보여줍니다.",
        basis:
          "각 줄 = 사건일지(incident) 한 건. 문제로 잡힌 시각부터 해소까지의 구간입니다.",
        rows: [
          { term: "시작 ~ 종료", desc: "문제 진입 ~ 해소 시각 (MM/DD HH:mm)" },
          { term: "지속 시간", desc: "종료된 구간의 문제 지속 길이" },
          { term: "코드 · [A4]", desc: "앞 = 카메라 ID, 뒤 badge = 구역 이름" },
        ],
        legend: [
          {
            swatch: "bg-status-majorOutage",
            label: "빨강 '진행 중' 배지 — 아직 안 풀린 구간",
          },
        ],
        period:
          "필터: 기간(1/3/7/30/전체) · 상태(전체/진행중/종료) · 건수. 최근 50건 내에서 필터.",
      }}
    >
      <FilterRow>
        <SegToggle<VideoRecentDaysFilter>
          value={daysFilter}
          onChange={setDaysFilter}
          options={[
            { value: 1, label: "1일" },
            { value: 3, label: "3일" },
            { value: 7, label: "7일" },
            { value: 30, label: "30일" },
            { value: 0, label: "전체" },
          ]}
          ariaLabel="기간 필터"
        />
        <SegToggle<VideoStatusFilter>
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "전체" },
            { value: "ongoing", label: "진행 중" },
            { value: "ended", label: "종료" },
          ]}
          ariaLabel="상태 필터"
        />
        <SegToggle<VideoRecentLimitFilter>
          value={limitFilter}
          onChange={setLimitFilter}
          options={[
            { value: 20, label: "20개" },
            { value: 50, label: "50개" },
          ]}
          ariaLabel="표시 건수"
        />
      </FilterRow>
      {isLoading && !data ? (
        <EmptyHint text="불러오는 중…" />
      ) : incidents.length === 0 ? (
        <EmptyHint text="조건에 맞는 문제 구간이 없습니다." />
      ) : (
        <ul className="space-y-1.5">
          {incidents.map((i, idx) => (
            <li
              key={`${i.lotId}-${i.zone}-${i.startedAt}-${idx}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
            >
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {lotLabel(i.lotName, i.lotId)}
              </span>
              {/* 카메라 = 주 라벨 (없으면 구역명 폴백) */}
              {i.cameras.length > 0 ? (
                <span className="flex flex-wrap items-center gap-x-1.5">
                  {i.cameras.map((c) => (
                    <span
                      key={c}
                      className="font-mono font-semibold text-slate-800 dark:text-slate-100"
                    >
                      {c}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="font-semibold text-slate-800 dark:text-slate-100">
                  {i.zone}
                </span>
              )}
              {/* 구역 = 보조 badge */}
              {i.cameras.length > 0 && (
                <span className="rounded-full border border-surface-border bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-surface-darkBorder dark:bg-slate-800/50 dark:text-slate-300">
                  {i.zone}
                </span>
              )}
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                {fmtTime(i.startedAt)} ~ {fmtTime(i.endedAt)}
              </span>
              {i.ongoing ? (
                <span className="rounded-full bg-status-majorOutage-bg px-2 py-0.5 text-[11px] font-medium text-status-majorOutage-fg dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg">
                  진행 중
                </span>
              ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {formatDuration(i.durationSec)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
